import { useState, useEffect, useRef } from 'react';
import { useBasket } from '../hooks/useBasket';
import { api, PO_NUMBER_MAX_LENGTH } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { attemptKeyFor } from '../lib/attemptKey';
import Recommendations from '../components/Recommendations';

// The quantity box. Emptying it to type a new number keeps the line; only a
// 0 left in the box (or the − button, or Remove) takes it out of the basket.
function QtyInput({ value, onChange }) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(String(value)); }, [value, focused]);
  return (
    <input
      style={styles.qtyInput}
      type="number"
      inputMode="numeric"
      min="0"
      value={text}
      placeholder="0"
      onFocus={() => setFocused(true)}
      onChange={e => {
        setText(e.target.value);
        const n = parseInt(e.target.value, 10);
        if (n > 0) onChange(n);
      }}
      onBlur={() => {
        setFocused(false);
        if (text.trim() === '0') onChange(0);
        else setText(String(value));
      }}
    />
  );
}

export default function Basket() {
  const { items, addItems, updateQuantity, removeItem, clearBasket, basketTotal, itemCount, markBasketViewed, editOrder, cancelEditOrder } = useBasket();
  const { user } = useAuth();

  // Once the user lands on the basket page, the mobile sticky basket bar
  // can stop pestering them — they've seen the basket. It'll reappear if
  // they add a new item later (addItems flips the flag back on).
  useEffect(() => {
    markBasketViewed();
  }, [markBasketViewed]);

  // Legacy customers (no role on token) and customer_admins can purchase
  // directly. Employees (role customer_user or anything else) submit for
  // an admin's approval unless explicitly granted canPurchaseDirectly.
  const canPurchaseDirectly = !user?.role
    || user?.role === 'customer_admin'
    || user?.permissions?.canPurchaseDirectly;
  // Staff looking as this customer (pages/StaffPreview.jsx); the server
  // refuses the order anyway.
  const isPreview = !!user?.preview;

  const [poNumber, setPoNumber] = useState('');
  const [poMissing, setPoMissing] = useState(false);
  const [requestedDate, setRequestedDate] = useState('');
  const [notes, setNotes] = useState('');

  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  // Address book — dropdown of delivery addresses (SOS default + customer's
  // custom entries). Defaults to whichever address the address-book service
  // marks as default; the user can swap before placing the order.
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const attemptRef = useRef(null);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadCustomer() {
      try {
        const data = await api.getCustomer();
        if (!cancelled) setCustomer(data);
      } catch (e) {
        // silently fail — address just won't show
      } finally {
        if (!cancelled) setCustomerLoading(false);
      }
    }
    async function loadAddresses() {
      try {
        const { addresses: list = [] } = await api.listAddresses();
        if (cancelled) return;
        setAddresses(list);
        const def = list.find(a => a.isDefault) || list[0];
        if (def) setSelectedAddressId(def.id);
      } catch (e) {
        // silently fail — picker will fall back to "no addresses available"
      }
    }
    loadCustomer();
    loadAddresses();
    return () => { cancelled = true; };
  }, []);

  const selectedAddress = addresses.find(a => a.id === selectedAddressId) || null;

  const setQty = (itemId, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    updateQuantity(itemId, n);
  };

  async function handleSubmit() {
    if (items.length === 0) return;
    // Every order needs something to reference it by. Not every customer runs
    // on PO numbers, so a reference or an email address counts too — the
    // backend enforces the same rule.
    const purchaseOrderRef = poNumber.trim();
    if (!purchaseOrderRef) {
      setPoMissing(true);
      setError('Please enter a purchase order number, reference, or email address before placing your order.');
      const field = document.querySelector('[data-po-input]');
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
      }
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const lines = items.map(item => ({
        itemId: item.sosId || item.itemId,
        quantity: item.quantity,
        price: item.price,
        // Send the longer catalog description as the SOS line description
        // (it's the detail the warehouse picks/packs by — e.g.
        // "150 pods per case"). Fall back to the product name when the
        // catalog has no description on the item.
        description: item.description || item.name,
        name: item.name,
        sku: item.sku,
      }));
      const order = { lines, poNumber: purchaseOrderRef, notes, requestedDate, selectedAddressId };
      const res = canPurchaseDirectly
        ? await api.placeOrder({ ...order, submissionKey: attemptKeyFor(attemptRef, order) })
        : await api.submitForApproval(order);
      attemptRef.current = null;
      // Tag the response so the success banner can pick the right copy
      setSubmitted({ ...res, isApproval: !canPurchaseDirectly });
      clearBasket();
      setPoNumber('');
      setPoMissing(false);
      setNotes('');
      setRequestedDate('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveEdit() {
    if (items.length === 0 || !editOrder) return;
    setSubmitting(true);
    setError('');
    try {
      const lines = items.map(item => ({
        itemId: item.sosId || item.itemId,
        quantity: item.quantity,
        price: item.price,
        description: item.description || item.name,
      }));
      const res = await api.updateOrder(editOrder.id, { lines });
      cancelEditOrder();
      setSubmitted({ ...res, message: res.message || `${editOrder.number} updated successfully.` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  // Canceling an edit must not strand the user on an empty basket page.
  // Drop edit mode (which also empties the basket) and send them back to
  // where they started the edit from — their order history.
  function handleCancelEdit() {
    cancelEditOrder();
    window.location.hash = '#order-history';
  }

  const shippingAddress = customer?.shipping;
  const customerName = customer?.name || user?.customerName || '';

  // Empty basket state
  if (items.length === 0 && !submitted) {
    return (
      <div style={styles.root}>
        <div style={styles.emptyCard}>
          <div style={styles.emptyIcon}>&#128722;</div>
          {editOrder ? (
            <>
              <h2 style={styles.emptyTitle}>No items in {editOrder.number}</h2>
              <p style={styles.emptyText}>Add items from the catalog, or cancel to leave this order unchanged. An order must have at least one item.</p>
              <a href="#catalog" style={styles.browseCatalogLink}>Browse Catalog</a>
              <button style={{ ...styles.clearBasketBtn, marginTop: '1rem', width: 'auto', padding: '0.6rem 1.25rem' }} onClick={handleCancelEdit}>Cancel Edit</button>
            </>
          ) : (
            <>
              <h2 style={styles.emptyTitle}>Your basket is empty</h2>
              <p style={styles.emptyText}>Add items from the catalog to get started.</p>
              <a href="#catalog" style={styles.browseCatalogLink}>Browse Catalog</a>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* Header */}
      <div data-page-header style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>{editOrder ? `Editing ${editOrder.number}` : 'Your Basket'}</h1>
          <p style={styles.subtitle}>{itemCount} item{itemCount !== 1 ? 's' : ''} &middot; ${basketTotal.toFixed(2)}</p>
        </div>
        <a
          href={`#${(() => { try { return sessionStorage.getItem('ss_last_browse_page') || 'order-guide'; } catch { return 'order-guide'; } })()}`}
          style={styles.continueShoppingLink}
        >&#8592; {editOrder ? 'Add More Items' : 'Continue Shopping'}</a>
      </div>

      {editOrder && (
        <div style={styles.editBanner}>
          <span>You are editing <strong>{editOrder.number}</strong>. Saving will update this existing order in our system.</span>
          <button style={styles.dismissBtn} onClick={handleCancelEdit} title="Cancel editing">Cancel Edit</button>
        </div>
      )}

      {submitted && (
        <div style={styles.successBanner}>
          <div>
            <span>&#10003; {submitted.message || (submitted.isApproval
              ? 'Order submitted for approval. Your admin has been notified.'
              : 'Order placed successfully!')}</span>
            <span style={{ marginLeft: '0.75rem' }}>
              <a href={submitted.isApproval ? '#approvals' : '#order-history'} style={styles.viewOrdersLink}>
                {submitted.isApproval ? 'View Pending Orders' : 'View Order History'}
              </a>
            </span>
          </div>
          <button style={styles.dismissBtn} onClick={() => setSubmitted(null)}>&#10005;</button>
        </div>
      )}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Items table — same data-product-table markers as the catalog so
          on mobile each row collapses into a stacked card. The shared CSS
          in index.html does the heavy lifting. */}
      {items.length > 0 && (
        <div data-product-table style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={th()}>Product</th>
                <th style={th('right')}>Unit Price</th>
                <th style={th('center')}>Quantity</th>
                <th style={th('right')}>Line Total</th>
                <th style={th('center')}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const lineTotal = (item.price || 0) * item.quantity;
                return (
                  <tr key={item.itemId} data-product-row style={styles.row}>
                    <td data-cell="product" style={styles.tdProduct}>
                      <div style={styles.productName}>{item.name}</div>
                      {item.description && item.description !== item.name && (
                        <div style={styles.productDescription}>{item.description}</div>
                      )}
                      <div style={styles.productMeta}>
                        {item.sku && <span style={styles.sku}>{item.sku}</span>}
                        {item.onSale && <span style={styles.saleBadge}>{item.saleLabel || 'SALE'}</span>}
                      </div>
                    </td>
                    <td data-cell="price" style={{ ...td('right'), fontFamily: 'monospace', color: '#A50B18' }}>
                      {item.onSale && item.originalPrice > item.price ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: 'rgba(0,0,0,0.35)', fontSize: '0.8em' }}>${item.originalPrice.toFixed(2)}</span>
                          <div>${item.price.toFixed(2)}</div>
                        </>
                      ) : (
                        item.price ? `$${item.price.toFixed(2)}` : '\u2014'
                      )}
                    </td>
                    <td data-cell="qty" style={td('center')}>
                      <div data-qty-control style={styles.qtyControl}>
                        <button style={styles.qtyBtn} onClick={() => setQty(item.itemId, item.quantity - 1)}>{'\u2212'}</button>
                        <QtyInput value={item.quantity} onChange={n => setQty(item.itemId, n)} />
                        <button style={styles.qtyBtn} onClick={() => setQty(item.itemId, item.quantity + 1)}>+</button>
                      </div>
                    </td>
                    <td data-cell="line-total" style={{ ...td('right'), fontFamily: 'monospace', color: item.quantity > 0 ? '#333' : 'rgba(0,0,0,0.2)' }}>
                      {item.quantity > 0 ? `$${lineTotal.toFixed(2)}` : '\u2014'}
                    </td>
                    <td data-cell="remove" style={td('center')}>
                      <button
                        data-remove-btn
                        style={styles.removeBtn}
                        title="Remove item"
                        onClick={() => removeItem(item.itemId)}
                      >{'\u00D7'}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pre-checkout cross-sell. Excludes what's already in the basket, and
          stays out of an edit-order flow — a customer amending a live SOS
          order is fixing something specific, not shopping. */}
      {items.length > 0 && !editOrder && (
        <Recommendations
          title="Often ordered with these"
          subtitle="Add before you check out"
          limit={4}
          exclude={items.map(i => String(i.sosId || i.itemId))}
          onAdded={addItems}
        />
      )}

      {/* Order Details + Shipping + Summary */}
      {items.length > 0 && (
        <div data-basket-bottom style={styles.bottomGrid}>
          {/* Left column: Order Details + Shipping */}
          <div style={styles.leftColumn}>
            {/* Order Details — only relevant when creating a new order */}
            {!editOrder && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Order Details</h3>
              <div style={styles.fieldGrid}>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>
                    Purchase Order Number / Reference / Email Address
                    <span style={styles.required}> *</span>
                  </label>
                  <input
                    data-po-input
                    style={{ ...styles.input, ...(poMissing ? styles.inputError : {}) }}
                    placeholder="PO number, reference, or your email address"
                    value={poNumber}
                    onChange={e => { setPoNumber(e.target.value); if (poMissing) setPoMissing(false); }}
                    maxLength={PO_NUMBER_MAX_LENGTH}
                    aria-invalid={poMissing || undefined}
                    aria-describedby="po-count"
                    required
                  />
                  <span id="po-count" style={styles.charCount}>
                    {poNumber.length}/{PO_NUMBER_MAX_LENGTH} characters
                  </span>
                  {poMissing && (
                    <span style={styles.fieldError}>Required — a PO number, a reference of your own, or your email address.</span>
                  )}
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Requested Delivery Date</label>
                  <input
                    style={styles.input}
                    type="date"
                    value={requestedDate}
                    onChange={e => setRequestedDate(e.target.value)}
                  />
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Notes</label>
                  <textarea
                    style={{ ...styles.input, resize: 'vertical', minHeight: 60 }}
                    placeholder="Any special instructions..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
            )}

            {/* Shipping Address — picker from the address book */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Deliver To</h3>
              {addresses.length > 0 ? (
                <>
                  <select
                    value={selectedAddressId || ''}
                    onChange={e => setSelectedAddressId(e.target.value)}
                    style={styles.addressSelect}
                  >
                    {addresses.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.label}{a.isDefault ? ' — Default' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedAddress && (
                    <p style={styles.addressText}>
                      {selectedAddress.line1}
                      {selectedAddress.line2 && <><br />{selectedAddress.line2}</>}
                      <br />
                      {[selectedAddress.city, selectedAddress.state].filter(Boolean).join(', ')} {selectedAddress.zip}
                      {selectedAddress.notes && <><br /><em style={{ color: 'rgba(0,0,0,0.55)' }}>{selectedAddress.notes}</em></>}
                    </p>
                  )}
                  <p style={styles.addressChangeText}>
                    <a href="#addresses" style={styles.mailtoLink}>Manage addresses</a>
                  </p>
                </>
              ) : customerLoading ? (
                <p style={styles.loadingText}>Loading...</p>
              ) : (
                <p style={styles.addressChangeText}>
                  No shipping address on file.{' '}
                  <a
                    href={`mailto:orders@silverservice.com?subject=Delivery Address Change - ${encodeURIComponent(customerName)}`}
                    style={styles.mailtoLink}
                  >
                    Contact us
                  </a>
                </p>
              )}
            </div>
          </div>

          {/* Right column: Order Summary */}
          <div data-basket-summary style={styles.summaryCard}>
            <h3 style={styles.cardTitle}>Order Summary</h3>
            <div style={styles.summaryLines}>
              {items.map(item => (
                <div key={item.itemId} style={styles.summaryLine}>
                  <span style={styles.summaryLineName}>{item.name}</span>
                  <span style={{ fontFamily: 'monospace', flexShrink: 0 }}>
                    {'\u00D7'}{item.quantity} = ${((item.price || 0) * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.summaryTotal}>
              <span>Subtotal</span>
              <span style={{ color: '#A50B18', fontWeight: 700 }}>${basketTotal.toFixed(2)}</span>
            </div>
            <p style={styles.summaryNote}>Final total may vary based on tax and any adjustments by Silver Service.</p>

            <div style={styles.actionButtons}>
              {editOrder ? (
                <>
                  <button
                    data-summary-place-order
                    style={styles.placeOrderBtn}
                    onClick={handleSaveEdit}
                    disabled={submitting || isPreview}
                  >
                    {submitting ? 'Saving...' : `Save Changes to ${editOrder.number}`}
                  </button>
                  <button
                    style={styles.clearBasketBtn}
                    onClick={handleCancelEdit}
                  >
                    Cancel Edit
                  </button>
                </>
              ) : (
              <>
              <button
                data-summary-place-order
                style={styles.placeOrderBtn}
                onClick={handleSubmit}
                disabled={submitting || isPreview}
              >
                {submitting
                  ? (canPurchaseDirectly ? 'Placing Order...' : 'Submitting...')
                  : (canPurchaseDirectly ? 'Place Order' : 'Submit for Approval')}
              </button>
              <button
                style={styles.clearBasketBtn}
                onClick={clearBasket}
              >
                Clear Basket
              </button>
              </>
              )}
              {isPreview && (
                <p style={styles.approvalNote}>
                  Staff preview: this is where the customer places the order. Nothing is sent from a preview.
                </p>
              )}
              {!editOrder && !canPurchaseDirectly && (
                <p style={styles.approvalNote}>
                  Your account submits orders for admin approval. The admin on your account will be notified by email and can approve or reject it.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile-only sticky action bar at the bottom of the screen.
          Always visible while scrolling so customers don't have to find
          their way back to the Order Summary card to place the order.
          Hidden on desktop (the Order Summary card stays the primary
          place-order affordance there). */}
      {items.length > 0 && (
        <div
          data-basket-action-bar
          style={styles.mobileActionBar}
          role="region"
          aria-label="Order action bar"
        >
          <div style={styles.mobileActionBarTotals}>
            <span style={styles.mobileActionBarLabel}>Subtotal</span>
            <span style={styles.mobileActionBarTotal}>${basketTotal.toFixed(2)}</span>
          </div>
          <button
            style={{ ...styles.mobileActionBarBtn, ...(submitting ? styles.mobileActionBarBtnBusy : {}) }}
            onClick={editOrder ? handleSaveEdit : handleSubmit}
            disabled={submitting || isPreview}
          >
            {editOrder
              ? (submitting ? 'Saving…' : 'Save Changes')
              : submitting
                ? (canPurchaseDirectly ? 'Placing…' : 'Submitting…')
                : (canPurchaseDirectly ? 'Place Order' : 'Submit for Approval')}
          </button>
        </div>
      )}
    </div>
  );
}

const th = (align = 'left') => ({
  padding: '0.75rem 1rem', textAlign: align,
  fontSize: '0.72rem', fontWeight: 600,
  color: 'rgba(0,0,0,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase',
  borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap',
});

const td = (align = 'left') => ({
  padding: '0.85rem 1rem', textAlign: align, verticalAlign: 'middle',
});

const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: '1rem' },

  // Empty state
  emptyCard: {
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
    padding: '3rem', textAlign: 'center', marginTop: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  emptyIcon: { fontSize: '3rem', marginBottom: '0.5rem' },
  emptyTitle: { fontSize: '1.25rem', fontWeight: 600, color: '#333', margin: '0 0 0.5rem' },
  emptyText: { fontSize: '0.9rem', color: 'rgba(0,0,0,0.45)', margin: '0 0 1.5rem' },
  browseCatalogLink: {
    display: 'inline-block', background: '#A50B18', color: '#fff',
    padding: '0.7rem 1.5rem', borderRadius: 6, fontSize: '0.9rem',
    fontWeight: 600, textDecoration: 'none', letterSpacing: '0.03em',
  },

  // Header
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: 0 },
  subtitle: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.4)', margin: '0.3rem 0 0' },
  continueShoppingLink: { fontSize: '0.85rem', color: '#A50B18', textDecoration: 'none', fontWeight: 500 },

  // Banners
  successBanner: {
    background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.25)',
    borderRadius: 8, padding: '1rem 1.25rem', color: '#27ae60',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  dismissBtn: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem' },
  editBanner: {
    background: 'rgba(165,11,24,0.06)', border: '1px solid rgba(165,11,24,0.25)',
    borderRadius: 8, padding: '0.85rem 1.25rem', color: '#A50B18',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
    fontSize: '0.88rem',
  },
  viewOrdersLink: { color: '#27ae60', fontWeight: 600, textDecoration: 'underline' },
  errorBanner: {
    background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)',
    borderRadius: 8, padding: '1rem 1.25rem', color: '#c0392b',
  },

  // Table
  tableWrap: {
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
    overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  row: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.1s' },
  tdProduct: { padding: '0.85rem 1rem', maxWidth: 320 },
  productName: { fontSize: '0.92rem', fontWeight: 500, color: '#333', marginBottom: 3 },
  // The longer SOS-line detail (e.g. "150 pods per case") — same text that
  // lands on the SOS sales order, shown so the basket matches the order.
  productDescription: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.55)', marginBottom: 4, lineHeight: 1.35 },
  productMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  sku: { fontSize: '0.72rem', color: 'rgba(0,0,0,0.35)', fontFamily: 'monospace' },
  saleBadge: { fontSize: '0.68rem', color: '#fff', background: '#A50B18', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 700, letterSpacing: '0.03em' },

  // Quantity controls (matches OrderGuide / Catalog)
  qtyControl: { display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' },
  qtyBtn: {
    width: 28, height: 28, background: '#f5f5f5', border: '1px solid #ddd',
    borderRadius: 4, color: '#333', cursor: 'pointer', fontSize: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qtyInput: {
    width: 56, textAlign: 'center', background: '#fff', border: '1px solid #ddd',
    borderRadius: 4, color: '#333', fontSize: '0.9rem', padding: '0.3rem',
    fontFamily: 'monospace', outline: 'none',
  },
  removeBtn: {
    background: 'none', border: 'none', color: 'rgba(0,0,0,0.3)',
    cursor: 'pointer', fontSize: '1.2rem', padding: '0.2rem 0.4rem',
    lineHeight: 1, transition: 'color 0.15s',
  },

  // Bottom grid: left column + summary
  bottomGrid: { display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  leftColumn: { flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: '1rem' },

  // Cards
  card: {
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
    padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTitle: { margin: '0 0 1rem', color: '#333', fontSize: '1rem', fontWeight: 600 },

  // Form fields (matches OrderGuide)
  fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)', fontWeight: 500, letterSpacing: '0.04em' },
  required: { color: '#A50B18', fontWeight: 700 },
  inputError: { border: '1px solid #A50B18', boxShadow: '0 0 0 2px rgba(165,11,24,0.12)' },
  fieldError: { fontSize: '0.72rem', color: '#A50B18', marginTop: '0.3rem' },
  charCount: { fontSize: '0.72rem', color: '#777', marginTop: '0.3rem' },
  input: {
    background: '#fff', border: '1px solid #ddd', borderRadius: 6,
    padding: '0.65rem 0.9rem', color: '#333', fontSize: '0.9rem',
    outline: 'none', fontFamily: 'inherit',
  },

  // Shipping address
  loadingText: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.4)', margin: 0 },
  addressSelect: { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #ddd', borderRadius: 5, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box', marginBottom: '0.7rem' },
  addressText: { fontSize: '0.9rem', color: '#333', margin: '0 0 0.75rem', lineHeight: 1.5 },
  addressChangeText: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.45)', margin: 0 },
  mailtoLink: { color: '#A50B18', textDecoration: 'underline' },

  // Order summary card
  summaryCard: {
    width: 340, minWidth: 300, flexShrink: 0,
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
    padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  summaryLines: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' },
  summaryLine: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)', gap: '0.5rem',
  },
  summaryLineName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  summaryTotal: {
    display: 'flex', justifyContent: 'space-between', fontSize: '1rem',
    fontWeight: 600, color: '#333', paddingTop: '0.5rem',
    borderTop: '1px solid #e0e0e0', marginTop: '0.5rem',
  },
  summaryNote: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.35)', margin: '0.5rem 0 0' },

  // Action buttons
  actionButtons: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' },
  placeOrderBtn: {
    width: '100%', background: '#A50B18', color: '#fff', border: 'none',
    borderRadius: 6, padding: '0.9rem', fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', letterSpacing: '0.03em',
  },
  clearBasketBtn: {
    width: '100%', background: '#fff', color: 'rgba(0,0,0,0.45)',
    border: '1px solid #ddd', borderRadius: 6, padding: '0.7rem',
    fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
  },
  // Sticky bottom action bar — mobile only (CSS unhides it via the
  // [data-basket-action-bar] selector in index.html @media). Always
  // visible on basket page so the place-order affordance never gets
  // lost off-screen. Padded for the iPhone home indicator.
  mobileActionBar: {
    display: 'none',
    position: 'fixed', left: 0, right: 0, bottom: 0,
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
    padding: '0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom, 0)) 1rem',
    alignItems: 'center', gap: '0.75rem',
    zIndex: 99,
  },
  mobileActionBarTotals: { display: 'flex', flexDirection: 'column', flexShrink: 0 },
  mobileActionBarLabel:  { fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  mobileActionBarTotal:  { fontSize: '1.1rem', fontFamily: 'monospace', fontWeight: 700, color: '#A50B18' },
  mobileActionBarBtn: {
    flex: 1, background: '#A50B18', color: '#fff', border: 'none',
    borderRadius: 6, padding: '0.85rem 1rem', fontSize: '0.95rem',
    fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em',
    fontFamily: 'inherit',
  },
  mobileActionBarBtnBusy: { background: 'rgba(165,11,24,0.65)', cursor: 'wait' },

  approvalNote: {
    fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)', lineHeight: 1.45,
    margin: '0.25rem 0 0', padding: '0.5rem 0.6rem',
    background: '#fef9e7', border: '1px solid #f5e6a3', borderRadius: 6,
  },
};
